import React, { Component } from 'react'
import Select from 'react-select'

// Native JS replacements for lodash functions
const isEmpty = (value) => {
  if (value == null) return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  if (typeof value === 'string') return value.length === 0;
  return false;
};

const sortedKeys = (obj) => Object.keys(obj || {}).sort();

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '36px',
    borderRadius: '8px',
    border: state.isFocused ? '1px solid #8b5cf6' : '1px solid #e2e8f0',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(139, 92, 246, 0.1)' : 'none',
    fontSize: '13px',
    backgroundColor: '#fff',
    '&:hover': {
      borderColor: '#a78bfa',
    },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#8b5cf6'
      : state.isFocused
      ? '#f5f3ff'
      : 'white',
    color: state.isSelected ? 'white' : '#334155',
    padding: '8px 12px',
    fontSize: '13px',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: '#8b5cf6',
    },
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: '8px',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    border: '1px solid #e2e8f0',
    zIndex: 50,
  }),
  menuList: (provided) => ({
    ...provided,
    borderRadius: '8px',
    padding: '4px',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#334155',
    fontWeight: '500',
    fontSize: '13px',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#94a3b8',
    fontSize: '13px',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: '6px',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
};

class CompactDrugFilter extends React.Component {
  constructor(options) {
    super()
    this.debounceTimer = null;
    this.initialSelections = {
      brand: options.currentSelections.brand,
      form: options.currentSelections.form,
      strength: options.currentSelections.strength,
      package: options.currentSelections.package,
      quantity: options.currentSelections.quantity
    };
    this.loadData(options.allOptions, options.currentSelections, this.state)
  }

  componentDidMount() {
    // Update search button state on mount
    this.updateSearchButtonState(false);
    // Expose instance globally for button click handler
    window.CompactDrugFilterInstance = this;
  }

  componentWillUnmount() {
    // Clear any pending timers
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    // Clean up global reference
    if (window.CompactDrugFilterInstance === this) {
      window.CompactDrugFilterInstance = null;
    }
  }

  loadData = (allOptions, selectedValues) => {
    let brandSelected, formSelected, strengthSelected, packageSelected, quantitySelected;
    let brands = [];
    let forms = [];
    let strengths = [];
    let packages = [];
    let quantities = [];

    sortedKeys(allOptions).forEach((name) => {
      const brandOption = { value: name, label: name };
      brands.push(brandOption);

      if (isEmpty(forms) && (name === selectedValues.brand || isEmpty(selectedValues.brand))) {
        brandSelected = brandOption;
        const formOptions = allOptions[brandSelected.value];

        sortedKeys(formOptions).forEach((name) => {
          const formOption = { value: name, label: name };
          forms.push(formOption);

          if (isEmpty(strengths) && (name === selectedValues.form || isEmpty(selectedValues.form))) {
            formSelected = formOption;
            const strengthOptions = formOptions[formSelected.value];

            sortedKeys(strengthOptions).forEach((name) => {
              const strengthOption = { value: name, label: name };
              strengths.push(strengthOption);

              if (isEmpty(packages) && (name === selectedValues.strength || isEmpty(selectedValues.strength))) {
                strengthSelected = strengthOption;
                const packageOptions = strengthOptions[strengthSelected.value];

                sortedKeys(packageOptions).forEach((name) => {
                  const packageOption = { value: name, label: name };
                  packages.push(packageOption);

                  if (isEmpty(quantities) && (name === selectedValues.package || isEmpty(selectedValues.package))) {
                    packageSelected = packageOption;
                    const quantityOptions = packageOptions[packageSelected.value];

                    sortedKeys(quantityOptions).forEach((name) => {
                      const quantityOption = { value: name, label: name };
                      quantities.push(quantityOption);

                      if (name === selectedValues.quantity || isEmpty(selectedValues.quantity)) {
                        quantitySelected = quantityOption;
                      }
                    });
                    // Sort quantities by name
                    quantities.sort((a, b) => (a.label || '').localeCompare(b.label || ''));
                  }
                });
              }
            });
          }
        });
      }
    });

    this.state = {
      allOptions,
      brands,
      brandSelected,
      forms,
      formSelected,
      strengths,
      strengthSelected,
      packages,
      packageSelected,
      quantities,
      quantitySelected
    };
  }

  hasFiltersChanged = () => {
    const current = {
      brand: this.state.brandSelected?.value,
      form: this.state.formSelected?.value,
      strength: this.state.strengthSelected?.value,
      package: this.state.packageSelected?.value,
      quantity: this.state.quantitySelected?.value
    };

    return (
      current.brand !== this.initialSelections.brand ||
      current.form !== this.initialSelections.form ||
      current.strength !== this.initialSelections.strength ||
      current.package !== this.initialSelections.package ||
      current.quantity !== this.initialSelections.quantity
    );
  }

  updateSearchButtonState = (hasChanged) => {
    const form = document.getElementById('drug-search-form');
    if (!form) return;

    const submitBtn = form.querySelector('button[type="submit"]');
    if (!submitBtn) return;

    if (hasChanged) {
      // Filters changed - enable button fully
      submitBtn.disabled = false;
      submitBtn.style.opacity = '1';
      submitBtn.style.cursor = 'pointer';
    } else {
      // No changes - dim button
      submitBtn.disabled = false;
      submitBtn.style.opacity = '0.6';
      submitBtn.style.cursor = 'pointer';
    }
  }

  scheduleRedirect = () => {
    // Clear any existing timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Check if filters have changed
    const hasChanged = this.hasFiltersChanged();
    this.updateSearchButtonState(hasChanged);

    if (!hasChanged) {
      return; // Don't schedule redirect if nothing changed
    }

    // Schedule redirect after 2 seconds
    this.debounceTimer = setTimeout(() => {
      this.redirectPage();
    }, 2000);
  }

  redirectPage = () => {
    const form = document.getElementById('drug-search-form');
    if (!form) return;

    const submitBtn = form.querySelector('button[type="submit"]');
    
    // Show loading state on button
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.innerHTML = `
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="animate-spin">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none" stroke-dasharray="60" stroke-dashoffset="20" opacity="0.25"></circle>
          <path d="M12 2 A10 10 0 0 1 22 12" stroke="currentColor" stroke-width="4" fill="none" stroke-linecap="round"></path>
        </svg>
        <span class="hidden sm:inline ml-2">Searching...</span>
      `;
    }

    // Show loading toast
    if (typeof Toast !== 'undefined') {
      Toast.loading('Updating prices...', { description: 'Checking 60,000+ pharmacies' });
    }

    const url = new URL(window.location.href);
    url.pathname = "/drug";
    url.searchParams.set('q', this.state.brandSelected.value);
    url.searchParams.set('form', this.state.formSelected.value);
    url.searchParams.set('strength', this.state.strengthSelected.value);
    url.searchParams.set('package', this.state.packageSelected.value);
    url.searchParams.set('qty', this.state.quantitySelected.value);

    // Preserve ZIP code if it exists
    const currentZip = new URLSearchParams(window.location.search).get('zip');
    if (currentZip) {
      url.searchParams.set('zip', currentZip);
    }

    window.location.href = url;
  }

  handleBrandChange = (brandSelected) => {
    this.loadData(this.state.allOptions, {
      brand: brandSelected.value,
    });
    this.setState(this.state);
    this.scheduleRedirect();
  }

  handleFormChange = (formSelected) => {
    this.loadData(this.state.allOptions, {
      brand: this.state.brandSelected.value,
      form: formSelected.value,
    });
    this.setState(this.state);
    this.scheduleRedirect();
  }

  handleStrengthChange = (strengthSelected) => {
    this.loadData(this.state.allOptions, {
      brand: this.state.brandSelected.value,
      form: this.state.formSelected.value,
      strength: strengthSelected.value,
    });
    this.setState(this.state);
    this.scheduleRedirect();
  }

  handlePackageChange = (packageSelected) => {
    this.loadData(this.state.allOptions, {
      brand: this.state.brandSelected.value,
      form: this.state.formSelected.value,
      strength: this.state.strengthSelected.value,
      package: packageSelected.value,
    });
    this.setState(this.state);
    this.scheduleRedirect();
  }

  handleQtyChange = (qtySelected) => {
    this.loadData(this.state.allOptions, {
      brand: this.state.brandSelected.value,
      form: this.state.formSelected.value,
      strength: this.state.strengthSelected.value,
      package: this.state.packageSelected.value,
      quantity: qtySelected.value,
    });
    this.setState(this.state);
    this.scheduleRedirect();
  }

  render() {
    return (
      <>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            Form
          </label>
          <Select
            value={this.state.formSelected}
            options={this.state.forms}
            onChange={this.handleFormChange}
            styles={customStyles}
            placeholder="Form..."
            aria-label="Medication form"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            Strength
          </label>
          <Select
            value={this.state.strengthSelected}
            options={this.state.strengths}
            onChange={this.handleStrengthChange}
            styles={customStyles}
            placeholder="Strength..."
            aria-label="Medication strength"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            Package
          </label>
          <Select
            value={this.state.packageSelected}
            options={this.state.packages}
            onChange={this.handlePackageChange}
            styles={customStyles}
            placeholder="Package..."
            aria-label="Package size"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            Quantity
          </label>
          <Select
            value={this.state.quantitySelected}
            options={this.state.quantities}
            onChange={this.handleQtyChange}
            styles={customStyles}
            placeholder="Qty..."
            aria-label="Quantity"
          />
        </div>
      </>
    );
  }
}

export default CompactDrugFilter
